import type { VehicleResponseType } from './types';
import { OpenHours } from './OpenHours/OpenHours';

export type VehicleTypeType = 'car' | 'truck';

export class Vehicle {
    private vehicle: VehicleResponseType | null = null;
    private color: string = '#ffffff';

    public get() {
        return this.vehicle;
    }

    public set(vehicle: VehicleResponseType): this {
        this.vehicle = vehicle;
        return this;
    }

    public getLastPosition(): { x: number; y: number; ts: string } | null {
        if (this.vehicle === null) return null;
        return this.vehicle.last_position;
    }

    public getName(): string {
        if (this.vehicle === null) return '';
        return this.vehicle.name;
    }

    public getId(): number | null {
        if (this.vehicle === null) return null;
        return this.vehicle.id;
    }

    public isActive() {
        if (this.vehicle === null) return false;
        return this.vehicle.active;
    }

    public setColor(color: string): this {
        this.color = color;
        return this;
    }

    public getColor(): string {
        return this.color;
    }

    public isTruck() {
        if (this.vehicle === null) return false;
        return (
            this.vehicle?.truck_attributes?.weight_t && this.vehicle.truck_attributes.weight_t > 3.5
        );
    }

    public getType(): VehicleTypeType {
        if (this.isTruck()) return 'truck';
        return 'car';
    }

    public getEmail() {
        if (this.vehicle === null) return '';
        return this.vehicle.email;
    }

    public getWeightLimit(): number | null {
        if (this.vehicle === null) return null;
        return this.vehicle.weight_limit;
    }

    public getParcelLimit(): number | null {
        if (this.vehicle === null) return null;
        return this.vehicle.parcel_limit;
    }

    public getTruckTotalWeight(): number | null {
        if (this.vehicle === null) return null;
        return this.vehicle?.truck_attributes?.weight_t ?? null;
    }

    public getTruckAxisLoad(): number | null {
        if (this.vehicle === null) return null;
        return this.vehicle?.truck_attributes?.load_t ?? null;
    }

    public getTruckLength(): number | null {
        if (this.vehicle === null) return null;
        return this.vehicle?.truck_attributes?.length_m ?? null;
    }

    public getTruckWidth(): number | null {
        if (this.vehicle === null) return null;
        return this.vehicle?.truck_attributes?.width_m ?? null;
    }

    public getTruckHeight(): number | null {
        if (this.vehicle === null) return null;
        return this.vehicle?.truck_attributes?.height_m ?? null;
    }

    public getTruckCategory(): number | null {
        if (this.vehicle === null) return null;
        return this.vehicle?.truck_attributes?.category ?? null;
    }

    public getTruckEuroNorm(): number | null {
        if (this.vehicle === null) return null;
        return this.vehicle?.truck_attributes?.euro_norm ?? null;
    }

    public getWarehouseId(): number | null {
        if (this.vehicle === null) return null;
        return this.vehicle.warehouse_id;
    }

    public calcAbbreviation(): string {
        // Bemowo -> Be
        // New York -> NY
        // Magazyn Tarnowo Podgórne -> TP
        let abbr = '';
        let name = this.getName().trim();
        if (name) {
            if (name.split(/\s+/).length > 1) {
                abbr = name
                    .split(/\s+/)
                    .map((w, idx, a) =>
                        !w.match(/magazyn/i) || a.length == 1 ? w[0].toUpperCase() : ''
                    )
                    .join('');
            } else {
                abbr = name[0].toUpperCase() + (name[1] ? name[1] : '');
            }

            if (abbr.length > 2) {
                abbr = abbr.slice(0, 2);
            }

            // if last letter is a number, add it to abbreviation
            // if (/\d/.test(name[name.length - 1])) {
            //     abbr += name[name.length - 1];
            // }
        }
        // this.setAbbreviation(abbr);
        return abbr;
    }

    public getDescription(): string {
        if (this.vehicle === null) return '';
        return this.vehicle.description;
    }

    public getWorkTimeForDay(day_yyyymmdd_hhiiss: string): number | null {
        if (this.vehicle === null) return null;
        if (this.vehicle.open_hours === null) return null;
        let day_yyyymmdd = day_yyyymmdd_hhiiss.slice(0, 10);
        return OpenHours.getWorkTimeDurationSeconds(this.vehicle.open_hours, day_yyyymmdd);
    }

    public getWorkTimeStartForDay(day_yyyymmdd_hhiiss: string): number | null {
        if (this.vehicle === null) return null;
        if (this.vehicle.open_hours === null) return null;
        let day_yyyymmdd = day_yyyymmdd_hhiiss.slice(0, 10);
        return OpenHours.getWorkTimeStartSeconds(this.vehicle.open_hours, day_yyyymmdd);
    }

    public getWorkTimeEndForDay(day_yyyymmdd_hhiiss: string): number | null {
        if (this.vehicle === null) return null;
        if (this.vehicle.open_hours === null) return null;
        let day_yyyymmdd = day_yyyymmdd_hhiiss.slice(0, 10);
        return OpenHours.getWorkTimeEndSeconds(this.vehicle.open_hours, day_yyyymmdd);
    }

    public getRouteCost(param: string|null = null) {
        if (param) {
            return this.vehicle?.route_cost?.[param];
        } else {
            return this.vehicle?.route_cost;
        }
    }
}
